import { paths } from '@/schemas/microcredit';
import axios from '@/shared/axios';

/**
 * 登录
 */
export type LoginType = DefineService<paths, '/user/login'>;
export function login(params: LoginType['Params']): Promise<LoginType['Result']> {
  return axios.post('/user/login', params, {
    __interceptorOptions__: {
      security: true,
    },
  });
}

/**
 * 极光号码认证登录
 */
export type LoginByPhoneVerifyType = DefineService<paths, '/user/phone/verify'>;
export function loginByPhoneVerify(
  params: LoginByPhoneVerifyType['Params'],
): Promise<LoginByPhoneVerifyType['Result']> {
  return axios.post('/user/phone/verify', params, {
    __interceptorOptions__: {
      security: true,
    },
  });
}

/**
 * 发送验证码
 */
export type SendCaptchaType = DefineService<paths, '/user/smsCode/request'>;
export function sendCaptcha(params: SendCaptchaType['Params']): Promise<void> {
  return axios.post(
    '/user/smsCode/request',
    {
      ...params,
      service: 'mobile_login',
    },
    {
      __interceptorOptions__: {
        security: true,
      },
    },
  );
}

/**
 * 注销发送验证码
 */
export type SendCloseAccountCaptchaType = DefineService<paths, '/user/smsCode/request'>;
export function sendCloseAccountCaptcha(
  params: SendCloseAccountCaptchaType['Params'],
): Promise<void> {
  return axios.post(
    '/user/smsCode/request',
    {
      ...params,
      service: 'user_cancel',
    },
    {
      __interceptorOptions__: {
        security: true,
      },
    },
  );
}

/**
 * 查询用户概况
 */
export type QueryUserOverviewType = DefineService<paths, '/loan/overview'>;
export function queryUserOverview(options?: {
  loading?: boolean;
}): Promise<QueryUserOverviewType['Result']> {
  return axios.post('/loan/overview', null, {
    __interceptorOptions__: {
      loading: options?.loading ?? true,
      security: true,
    },
  });
}

/**
 * 查询广告主用户概况
 */
export type QueryPartnerUserOverviewType = DefineService<
  paths,
  '/diversion/home/diversionOverview'
>;
export function queryPartnerUserOverview(options?: {
  loading?: boolean;
}): Promise<QueryPartnerUserOverviewType['Result']> {
  return axios.post('/diversion/home/diversionOverview', null, {
    __interceptorOptions__: {
      loading: options?.loading ?? true,
      security: true,
    },
  });
}

/**
 * 查询用户信息
 */
export type QueryProfileType = DefineService<paths, '/user/info'>;
export function queryProfile(options?: {
  shouldLogin?: boolean;
}): Promise<QueryProfileType['Result']> {
  return axios.post('/user/info', null, {
    __interceptorOptions__: {
      security: true,
      shouldLogin: options?.shouldLogin ?? false,
    },
  });
}

/**
 * 退出
 */
export function logout() {}

/**
 * 查询用户还款事件提醒
 */
export type QueryRepayEventRemindersType = DefineService<paths, '/repay/getRepayDateList', 'get'>;
export function queryRepayEventReminders(): Promise<QueryRepayEventRemindersType['Result']> {
  return axios.get('/repay/getRepayDateList');
}

/**
 * 解密参数获取手机号
 */
export type DecryptPhoneNumberType = DefineService<paths, '/tf/landingPage/decryptMobile'>;
export function decryptPhoneNumber(
  params: DecryptPhoneNumberType['Params'],
): Promise<DecryptPhoneNumberType['Result']> {
  return axios.post('/tf/landingPage/decryptMobile', params, {
    __interceptorOptions__: {
      security: true,
      toast: false,
    },
  });
}

/**
 * 查询用户弹窗中心
 */
export type QueryUserPopupCenterInfo = DefineService<paths, '/user/message/pop/center', 'get'>;
export type QueryUserPopupCenterInfoResult = NonNullable<
  QueryUserPopupCenterInfo['Result']['popContentBOList']
>[number] & {
  activityUrl: string;
};
export function queryUserPopupCenterInfo(
  pageMark: string,
  action?: 'back' | 'enter' | 'all', // 不传或者传all时, 返回所有弹窗
): Promise<QueryUserPopupCenterInfo['Result']> {
  return axios.get('/user/message/pop/center', {
    params: {
      pageMark,
      action,
    },
  });
}

export type UserMessagePopCenter = DefineService<paths, '/user/message/pop/center'>;
export type PopContent = NonNullable<UserMessagePopCenter['Result']['popContentBOList']>[number];

/**
 * 查询用户弹窗中心
 */
export function userMessagePopCenter(
  params: UserMessagePopCenter['Params'],
): Promise<UserMessagePopCenter['Result']> {
  return axios.post('/user/message/pop/center', params, {
    __interceptorOptions__: {
      loading: false,
      toast: false,
    },
  });
}

export type RecordPopClicks = DefineService<paths, '/user/message/pop/recordClicks'>;

/**
 * 记录用户弹窗已打开
 */
export function recordPopClicks(params: RecordPopClicks['Params']): Promise<void> {
  return axios.post('/user/message/pop/recordClicks', params, {
    __interceptorOptions__: {
      loading: false,
      toast: false,
    },
  });
}

/**
 * 检查是否绑定微信
 */
export type CheckWeChatBindingType = DefineService<paths, '/user/wechat/checkBind'>;
export function checkWeChatBinding(
  params: CheckWeChatBindingType['Params'],
): Promise<CheckWeChatBindingType['Result']> {
  return axios.post('/user/wechat/checkBind', params, {
    __interceptorOptions__: {
      shouldLogin: false,
    },
  });
}

/**
 * 获取指定页面分流配置
 */
export interface QueryPageDiversionType {
  Params: {
    pageMark: string;
    action?: string;
    actionList?: string[];
  };
  Result: {
    insurancePageSwitch: boolean;
    insurancePopupSwitch: boolean;
    contractPageScreenRecordSwitch: boolean;
    contractPageCustomerServiceSwitch: boolean;
  };
}
export function queryPageDiversion(
  params: QueryPageDiversionType['Params'],
): Promise<QueryPageDiversionType['Result']> {
  return axios.get('/user/page/diversion', { params });
}

/*
 * 获取用户页面编码
 */
export type UserPageCodeType = DefineService<paths, '/user/page_code'>;
export function queryUserPageCode(
  params: UserPageCodeType['Params'],
): Promise<UserPageCodeType['Result']> {
  return axios.post('/user/page_code', params);
}
